import { getPreferences } from '@orbit-mind/engine-react';
import type { ThemeId } from '@orbit-mind/shared-types';
import { folderInTrash, isFavorite, placeMap, setFavorite } from '@orbit-mind/domain';
import {
  useLibraryOrganization,
  useLibraryIndex,
  useLibrarySyncStatus,
} from '@orbit-mind/engine-react';
import { libraryStore, libraryActions, syncOrganizationOf } from './organization';
import { FolderExplorer } from './FolderExplorer';
import { MapActions } from './MapActions';
import { createFileSync } from '../engine/fileSync';
import { downloadLibraryBackup } from '../engine/backup';
/**
 * The library.
 *
 * Reads the local catalogue, which holds one row per map and no documents — so a hundred maps
 * render without a hundred documents being opened. What this screen lists is still what *this
 * browser* holds: a map reaches the server while it is open, because that is when there is an
 * engine draining its outbox, and the copy says exactly that rather than implying a library that
 * follows somebody between devices.
 *
 * The account is read here rather than inside `AccountControl` because two things need it — the
 * control in the corner and the line under the heading — and two components reading it would be
 * two requests and, for a moment, two answers.
 *
 * It is also the first thing anybody sees, which is why it carries the product's name and mark
 * and why the empty state is an invitation rather than a status line. A library with no maps in
 * it is the most common state this screen will ever be in — every user starts there.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { createDocument, type MapDocument } from '@orbit-mind/domain';
import { randomIds } from '@orbit-mind/map-engine';
import type { MapSummary } from '@orbit-mind/offline-engine/indexeddb';
import {
  storageScope,
  type AppError,
  type LayoutId,
  type MapId,
  type Timestamp,
} from '@orbit-mind/shared-types';
import { instantiate, type Template } from '@orbit-mind/template-catalog';

import { syncLibrary } from '@orbit-mind/sync-engine';

import { AccountControl } from '../account/AccountControl';
import { ConsentBanner } from '../telemetry/ConsentBanner';
import { count } from '../telemetry/telemetry';
import { useAccount, type AccountView } from '../account/useAccount';
import { setLocale, useLocale } from '@orbit-mind/engine-react';
import { MapThumbnail } from './MapThumbnail';
import {
  storageAccount,
  localStorageLayer,
  storageIsAvailable,
  whenStorageIsIdle,
} from '../engine/storage';
import { LOCAL_AUTHOR, LOCAL_WORKSPACE } from '../engine/useOpenMap';
import { Button, IconButton } from '../ui/controls';
import { IconOrbit, IconPlus, IconTrash } from '../ui/icons';
import { CreateMapDialog } from './CreateMapDialog';

/**
 * A map id, minted where a map is created.
 *
 * Node and edge ids come from the engine's id source; a map id does not, because a map exists
 * before an engine is opened over it. Same shape, on purpose — an id is opaque either way.
 */
function newMapId(): MapId {
  return `map_${randomIds().nodeId()}` as MapId;
}

const now = (): Timestamp => Date.now() as Timestamp;

/** One transport for the screen. Nothing here holds state, so nothing needs one per render. */

/**
 * What the line under the heading says.
 *
 * Four states and four sentences, because the one thing this line must never do is imply that
 * signing in moved anything. It has not: the maps are in this browser in every one of these
 * states, and saying so is the same rule the save-state badge follows.
 */
function storageNote(account: AccountView): string {
  if (account.status === 'signed-in') {
    // Edits are sent while a map is open, and maps from other devices arrive when this screen
    // loads. "Synced with your account" is both halves; "always" would be a promise about a map
    // that is sitting closed with unsent work in it.
    return `Signed in as ${account.user.email}. Stored in this browser and synced with your account.`;
  }
  if (account.status === 'signed-out') {
    return 'Guest library on this device. Signing in opens a separate account library.';
  }
  return 'Your maps are saved on this device and work offline.';
}

export function LibraryPage(): React.JSX.Element {
  const { locale, text: t } = useLocale();
  const [layer] = useState(() => localStorageLayer());
  const favoritesKey = `orbit-mind.favorites.${storageScope(storageAccount())}`;
  const [syncTransport] = useState(() => createFileSync().transport);
  const [backupBusy, setBackupBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'all' | 'favorites' | 'trash'>('all');
  const [sort, setSort] = useState<'recent' | 'name' | 'nodes' | 'size'>('recent');
  const [presentation, setPresentation] = useState<'grid' | 'list'>('grid');
  const [folder, setFolder] = useState<string | null>(null);
  const [chosen, setChosen] = useState<MapSummary | null>(null);
  const [store] = useState(() => libraryStore(layer));
  const [actions] = useState(() => libraryActions(layer));
  const organization = useLibraryOrganization(store);
  // Favourites are part of the organization, so they travel with the folders and tags.
  const favorites = Object.keys(organization.library.placements).filter((id) =>
    isFavorite(organization.library, id),
  );
  /*
   * Favourites this browser kept on its own before they were part of the organization. Moved in
   * once, then forgotten here: from then on they sync like everything else in it.
   */
  const updateOrganization = organization.update;
  useEffect(() => {
    if (!organization.ready) return;
    let legacy: string[];
    try {
      const value: unknown = JSON.parse(localStorage.getItem(favoritesKey) ?? '[]');
      legacy = Array.isArray(value)
        ? value.filter((id): id is string => typeof id === 'string')
        : [];
    } catch {
      return;
    }
    if (legacy.length === 0) return;
    void updateOrganization((previous) =>
      legacy.reduce((library, id) => setFavorite(library, id, true), previous),
    ).then((saved) => {
      if (!saved) return;
      try {
        localStorage.removeItem(favoritesKey);
      } catch {
        // Moved already; a copy left behind is read as the same favourites next time.
      }
    });
    // Once the organization has been read: `update` is a new function on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.ready, favoritesKey]);
  const [syncNotice, setSyncNotice] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const account = useAccount();
  const [maps, setMaps] = useState<readonly MapSummary[]>([]);
  const syncStates = useLibrarySyncStatus(
    maps,
    account.state.status === 'signed-in',
    actions.pending,
  );
  const { index: searchIndex, indexing } = useLibraryIndex(
    maps,
    actions.read,
    query.trim() !== '' || sort === 'size',
  );
  const [damagedCount, setDamagedCount] = useState(0);
  const [error, setError] = useState<AppError | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  /** True from the moment a map is asked for until the route changes, so it cannot be asked twice. */
  const [creating, setCreating] = useState(false);

  /**
   * One loading path, re-run by bumping a token.
   *
   * The alternative — an async `refresh` that event handlers and an effect both call — has two
   * problems that look like one. It sets state after an await with nothing to say whether the
   * component is still mounted, and it gives the same job two entry points that can interleave.
   * A token means the effect owns loading, and everything else asks for a reload.
   */
  const [reloadToken, setReloadToken] = useState(0);
  const refresh = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  /*
   * `?signin=failed`, which is the only thing the Worker tells a browser about a sign-in that did
   * not complete. Deliberately one marker for five different refusals — a state that did not
   * match, an expired token, a provider that vouched for no address — because telling somebody
   * which of them they hit tells a prober how close they got.
   *
   * Read once and then taken out of the URL: it belongs to the redirect that carried it, and a
   * page reloaded an hour later should not still be reporting a sign-in from this morning.
   *
   * The notice is remembered at mount rather than read from the parameter on every render, which
   * is not a detail. Rendering straight off the URL and then clearing it means the message appears
   * and vanishes in the same frame — a flash nobody can read, and a test that cannot see it
   * either. An initialiser rather than an effect that sets state, because state set inside an
   * effect is a second render of the whole screen to say something that was already known.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const [signInFailed] = useState(() => searchParams.get('signin') === 'failed');
  useEffect(() => {
    if (searchParams.get('signin') !== 'failed') return;
    const next = new URLSearchParams(searchParams);
    next.delete('signin');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      // A map closed a moment ago is still writing its row. Reading past it would show the
      // numbers this screen exists to report, one edit out of date.
      await whenStorageIsIdle();
      const listing = await layer.catalog.list({ includeTrashed: true });
      if (cancelled) return;

      if (!listing.ok) {
        setError(listing.error);
        setLoading(false);
        return;
      }
      setMaps(listing.value.maps);
      setDamagedCount(listing.value.damaged.length);
      setError(null);
      setLoading(false);
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [reloadToken, layer]);

  /*
   * Maps from other devices.
   *
   * Once per sign-in, after the local listing is on screen: the library never waits on the
   * network to show what is already here. Anything that arrives is written down locally first
   * and the listing re-read, so what the screen shows is still only ever what this browser holds.
   */
  const signedInAs = account.state.status === 'signed-in' ? account.state.user.email : null;
  const reloadOrganization = organization.reload;
  useEffect(() => {
    if (signedInAs === null) return;
    let cancelled = false;
    const isCancelled = (): boolean => cancelled;

    const pull = async (): Promise<void> => {
      await whenStorageIsIdle();
      const outcome = await syncLibrary({
        transport: syncTransport,
        isCancelled,
        catalog: layer.catalog,
        journal: layer.journal,
        now,
      });
      if (isCancelled()) return;
      // Folders, tags and favourites after the maps they file.
      const organized = await syncOrganizationOf(layer, isCancelled);
      if (isCancelled()) return;
      if (organized.ok && organized.value.received) reloadOrganization();
      setSyncNotice(!outcome.ok || outcome.value.failed > 0 || !organized.ok);
      if (!outcome.ok) return;
      if (outcome.value.added + outcome.value.updated > 0) refresh();
    };

    void pull();
    const retry = (): void => {
      void pull();
    };
    window.addEventListener('online', retry);
    return () => {
      cancelled = true;
      window.removeEventListener('online', retry);
    };
  }, [signedInAs, refresh, reloadToken, syncTransport, layer, reloadOrganization]);

  /**
   * Writing a map down and opening it.
   *
   * One path for both ways of starting, because everything after "here is a document" is
   * identical — and a second copy of it would be a second place for the checkpoint and the
   * listing row to fall out of step. `catalog.create` writes both together, which is what makes
   * the map openable at all.
   */
  const open = useCallback(
    async (document: MapDocument) => {
      setCreating(true);
      const created = await layer.catalog.create(document, now());
      if (!created.ok) {
        setCreating(false);
        setError(created.error);
        return;
      }
      if (folder !== null)
        await organization.update((previous) => placeMap(previous, document.map.id, folder, ''));
      setDialogOpen(false);
      // One event for every way of starting — blank, template, or drafted by a model — because
      // the funnel's question is "did a map come to exist", not "which button".
      count({ kind: 'funnel', event: 'map-created' });
      await navigate(`/map/${document.map.id}`);
    },
    [navigate, layer, folder, organization],
  );

  const createBlank = useCallback(
    async (layoutId: LayoutId = getPreferences().layout, title = '') => {
      const mapId = newMapId();
      await open(
        createDocument({
          mapId,
          rootNodeId: randomIds().nodeId(),
          workspaceId: LOCAL_WORKSPACE,
          ownerId: LOCAL_AUTHOR,
          now: now(),
          title: title === '' ? 'Untitled map' : title,
          layoutId,
          themeId: getPreferences().palette as ThemeId,
        }),
      );
    },
    [open],
  );

  const createFromTemplate = useCallback(
    async (template: Template) => {
      const ids = randomIds();
      // A copy with fresh ids: the template's own ids belong to the template, and two maps made
      // from one that shared them could not both be synced. `instantiate` is where that happens.
      await open(
        instantiate(template, {
          mapId: newMapId(),
          nodeId: ids.nodeId,
          edgeId: ids.edgeId,
          ownerId: LOCAL_AUTHOR,
          workspaceId: LOCAL_WORKSPACE,
          now: now(),
        }),
      );
    },
    [open],
  );

  const trash = useCallback(
    async (mapId: MapId) => {
      const result = await layer.catalog.moveToTrash(mapId, now());
      if (!result.ok) setError(result.error);
      refresh();
    },
    [refresh, layer],
  );

  const needle = query.trim().toLocaleLowerCase(locale);
  const shown = maps
    .filter((summary) => {
      const placed = organization.library.placements[summary.map.id];
      if (view === 'trash') {
        if (summary.map.deletedAt === null) return false;
      } else if (
        summary.map.deletedAt !== null ||
        folderInTrash(organization.library, placed?.folderId ?? null)
      )
        return false;
      if (view === 'favorites' && !favorites.includes(summary.map.id)) return false;
      if (view === 'all' && !needle && (placed?.folderId ?? null) !== folder) return false;
      return (
        !needle ||
        (
          summary.map.title +
          ' ' +
          (placed?.tags.join(' ') ?? '') +
          ' ' +
          (searchIndex[summary.map.id]?.text ?? '')
        )
          .toLocaleLowerCase(locale)
          .includes(needle)
      );
    })
    .sort((a, b) =>
      sort === 'name'
        ? a.map.title.localeCompare(b.map.title, locale)
        : sort === 'nodes'
          ? b.nodeCount - a.nodeCount
          : sort === 'size'
            ? (searchIndex[b.map.id]?.bytes ?? 0) - (searchIndex[a.map.id]?.bytes ?? 0)
            : b.map.updatedAt - a.map.updatedAt,
    );
  function favorite(id: MapId): void {
    void organization.update((previous) => setFavorite(previous, id, !isFavorite(previous, id)));
  }
  async function restore(id: MapId): Promise<void> {
    const result = await layer.catalog.restoreFromTrash(id);
    if (!result.ok) setError(result.error);
    refresh();
  }
  if (!storageIsAvailable()) {
    return (
      <Shell>
        <Notice tone="danger">
          This browser will not let Orbit Mind store anything. Nothing you type here would survive
          closing the tab, so the editor is not offered. Private browsing is the usual cause.
        </Notice>
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <span className="text-prisma mb-1.5 flex items-center gap-2 text-xs font-semibold tracking-[0.14em] uppercase">
            <IconOrbit size={32} className="text-accent shrink-0" />
            Orbit Mind
          </span>
          <h1 className="text-[1.75rem] leading-tight font-semibold sm:text-3xl">
            {t('Your maps', 'Seus mapas')}
          </h1>
          <p className="text-ink-muted mt-1.5 text-sm">
            {locale === 'pt-BR'
              ? account.state.status === 'signed-in'
                ? 'Mapas salvos neste dispositivo e sincronizados com sua conta.'
                : 'Suas ideias, salvas neste dispositivo. Continue mesmo sem internet.'
              : storageNote(account.state)}
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <Button
            onClick={() => {
              void navigate('/settings');
            }}
          >
            {t('Settings', 'Configurações')}
          </Button>
          <AccountControl
            state={account.state}
            onSignOut={account.endSession}
            onRefresh={account.refresh}
            // Where the provider sends the browser back to. The path being looked at, so a
            // sign-in started from a map returns to that map rather than to the library.
            returnTo={`${location.pathname}${location.search}`}
          />
          <Button
            tone="primary"
            size="lg"
            onClick={() => {
              setDialogOpen(true);
            }}
          >
            <IconPlus size={18} />
            {t('New map', 'Novo mapa')}
          </Button>
        </div>
      </header>

      <div className="mb-7 flex flex-wrap items-center gap-3">
        <input
          type="search"
          aria-label={t('Search your maps', 'Buscar seus mapas')}
          placeholder={t('Search your maps', 'Buscar seus mapas')}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          className="border-edge bg-surface min-h-11 min-w-40 flex-1 rounded-xl border px-4 text-sm"
        />
        <select
          aria-label={t('Sort maps', 'Ordenar mapas')}
          value={sort}
          onChange={(event) => {
            setSort(event.target.value as typeof sort);
          }}
          className="border-edge bg-surface min-h-11 rounded-xl border px-3 text-sm"
        >
          <option value="recent">{t('Recently edited', 'Editados recentemente')}</option>
          <option value="name">{t('Name', 'Nome')}</option>
          <option value="nodes">{t('Number of nodes', 'Número de nós')}</option>
          <option value="size">{t('Document size', 'Tamanho do documento')}</option>
        </select>
        <select
          aria-label="Language / Idioma"
          value={locale}
          onChange={(event) => {
            setLocale(event.target.value === 'pt-BR' ? 'pt-BR' : 'en');
          }}
          className="border-edge bg-surface min-h-11 rounded-xl border px-3 text-sm"
        >
          <option value="pt-BR">Português</option>
          <option value="en">English</option>
        </select>
        <Button
          onClick={() => {
            setPresentation(presentation === 'grid' ? 'list' : 'grid');
          }}
        >
          {presentation === 'grid' ? t('List view', 'Ver lista') : t('Grid view', 'Ver cartões')}
        </Button>
        <div className="flex w-full flex-wrap gap-2">
          <Button
            disabled={backupBusy}
            onClick={() => {
              if (backupBusy) return;
              setBackupBusy(true);
              void whenStorageIsIdle()
                .then(() => downloadLibraryBackup(layer))
                .catch((cause: unknown) => {
                  window.alert(cause instanceof Error ? cause.message : 'Falha no backup.');
                })
                .finally(() => {
                  setBackupBusy(false);
                });
            }}
          >
            {backupBusy
              ? t('Preparing backup…', 'Preparando backup…')
              : t('Backup entire library', 'Backup da biblioteca inteira')}
          </Button>
          {(['all', 'favorites', 'trash'] as const).map((value) => (
            <Button
              key={value}
              tone={view === value ? 'primary' : 'ghost'}
              onClick={() => {
                setView(value);
              }}
            >
              {value === 'all'
                ? t('All maps', 'Todos os mapas')
                : value === 'favorites'
                  ? t('Favorites', 'Favoritos')
                  : t('Trash', 'Lixeira')}
            </Button>
          ))}
        </div>
      </div>
      <FolderExplorer
        organization={organization}
        current={folder}
        onOpen={(id) => {
          setFolder(id);
          setQuery('');
        }}
        maps={maps}
        query={query}
        trash={view === 'trash'}
        synced={signedInAs !== null}
      />
      {indexing && (
        <p role="status" className="text-ink-muted text-xs">
          {t('Searching map contents…', 'Buscando no conteúdo dos mapas…')}
        </p>
      )}
      {organization.error && <p role="alert">{organization.error}</p>}
      {syncNotice && (
        <Notice tone="warning">
          {t(
            'Some changes are waiting to sync. Your local maps are safe.',
            'Algumas alterações aguardam sincronização. Seus mapas locais estão seguros.',
          )}{' '}
          <button className="underline" onClick={refresh}>
            {t('Retry', 'Tentar novamente')}
          </button>
        </Notice>
      )}
      {signInFailed && (
        <Notice tone="warning">
          That sign-in did not finish, and nothing has changed. Your maps are where you left them;
          try again when you are ready.
        </Notice>
      )}

      {error !== null && <Notice tone="danger">{error.message}</Notice>}

      {damagedCount > 0 && (
        <Notice tone="warning">
          {damagedCount === 1 ? '1 map' : `${String(damagedCount)} maps`} could not be read back and{' '}
          {damagedCount === 1 ? 'is' : 'are'} not listed. Nothing has been deleted.
        </Notice>
      )}

      {loading ? (
        <p className="text-ink-faint animate-fade-in text-sm">Reading the library…</p>
      ) : shown.length === 0 ? (
        view !== 'all' || query !== '' ? (
          <p className="text-ink-muted py-10 text-center">
            {t('No maps here yet.', 'Nenhum mapa por aqui.')}
          </p>
        ) : (
          <EmptyLibrary
            onCreate={() => {
              setDialogOpen(true);
            }}
          />
        )
      ) : (
        <ul
          className={
            presentation === 'grid' ? 'grid gap-5 sm:grid-cols-2 lg:grid-cols-3' : 'grid gap-3'
          }
        >
          {shown.map((summary, index) => (
            <li
              key={summary.map.id}
              className="rounded-panel border-edge bg-surface animate-rise-in hover:border-edge-strong group relative flex flex-col items-stretch gap-3 border p-4 transition-[border-color,box-shadow] hover:shadow-md"
              // Staggered, but only just: eight rows arriving at once reads as a flicker, and
              // eight arriving over a second reads as a load screen. Capped so a library of a
              // hundred maps does not animate for four seconds.
              style={{ animationDelay: `${String(Math.min(index, 8) * 35)}ms` }}
            >
              {presentation === 'grid' && <MapThumbnail summary={summary} />}
              <p className="text-ink-muted text-xs">
                {syncStates[summary.map.id] === 'sent'
                  ? t('Map sent to cloud', 'Mapa enviado à nuvem')
                  : syncStates[summary.map.id] === 'pending'
                    ? t('Pending changes', 'Alterações pendentes')
                    : syncStates[summary.map.id] === 'local'
                      ? t('On this device', 'Neste dispositivo')
                      : syncStates[summary.map.id] === 'unavailable'
                        ? t('Sync status unavailable', 'Estado de envio indisponível')
                        : t('Checking…', 'Verificando…')}
              </p>
              {organization.library.placements[summary.map.id]?.tags.length ? (
                <p className="text-accent text-xs">
                  {organization.library.placements[summary.map.id]?.tags.join(' · ')}
                </p>
              ) : null}

              <button
                type="button"
                onClick={() => {
                  if (view !== 'trash') void navigate(`/map/${summary.map.id}`);
                }}
                disabled={view === 'trash'}
                className="min-w-0 flex-1 text-left"
              >
                {/* The whole card is the target, not just the words on it. */}
                <span className="absolute inset-0 rounded-[inherit]" aria-hidden="true" />
                <span className="block truncate font-medium">{summary.map.title}</span>
                <span className="text-ink-faint mt-0.5 block text-xs">
                  {summary.nodeCount} {t('ideas', 'ideias')} ·{' '}
                  {new Date(summary.map.updatedAt).toLocaleDateString(locale)}
                </span>
              </button>

              <div className="relative z-10 flex items-center justify-between">
                {view === 'trash' ? (
                  <Button
                    onClick={() => {
                      void restore(summary.map.id);
                    }}
                  >
                    {t('Restore', 'Restaurar')}
                  </Button>
                ) : (
                  <IconButton
                    label={t('Favorite', 'Favorito')}
                    icon={
                      <span aria-hidden="true">
                        {favorites.includes(summary.map.id) ? '★' : '☆'}
                      </span>
                    }
                    aria-pressed={favorites.includes(summary.map.id)}
                    onClick={() => {
                      favorite(summary.map.id);
                    }}
                  />
                )}
                {view !== 'trash' && (
                  <Button
                    aria-label={t('Manage map', 'Gerenciar mapa') + ' ' + summary.map.title}
                    onClick={() => {
                      setChosen(summary);
                    }}
                  >
                    ⋯
                  </Button>
                )}
                {view !== 'trash' && (
                  <IconButton
                    label={'Move ' + summary.map.title + ' to trash'}
                    icon={<IconTrash size={17} />}
                    className="hover:text-danger"
                    onClick={() => void trash(summary.map.id)}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <footer className="border-edge text-ink-faint mt-12 border-t pt-4 text-xs">
        Orbit Mind ·{' '}
        {t('From ideas to understanding and action.', 'De ideias a conhecimento e ação.')} ·{' '}
        <a className="underline" href="/privacy">
          {t('Privacy', 'Privacidade')}
        </a>
      </footer>

      {/*
        The consent question, on the library and not over the editor.
        *
        It was app-level for about an hour, and the responsive acceptance suite caught what that
        meant: `fixed bottom-3` on a 375px phone sits exactly on top of the editor's bottom bar,
        and "Add" could not be pressed. The library is where somebody arrives, nothing is covered
        here, and a question asked at the door is the one people answer rather than dismiss.
        *
        Mounting it inside a route does not make it ask twice: the answer is stored, and the
        banner reads it at mount.
      */}
      <ConsentBanner />
      {chosen && (
        <MapActions
          key={chosen.map.id}
          summary={chosen}
          organization={organization}
          actions={actions}
          onClose={() => {
            setChosen(null);
          }}
          onRefresh={refresh}
        />
      )}

      <CreateMapDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreateBlank={(layoutId, title) => void createBlank(layoutId, title)}
        onCreateFromTemplate={(template) => void createFromTemplate(template)}
        // The document the preview showed, through the same `open` every other way of starting
        // goes through. There is no second path for a document to become a map, which is what
        // makes "accepting a draft" the ordinary thing rather than a special case.
        onCreateGenerated={(document) => void open(document)}
        generateAvailable={account.state.status === 'signed-in'}
        busy={creating}
      />
    </Shell>
  );
}

/**
 * The first thing most people will see.
 *
 * A button, not a sentence. "No maps yet" is a true statement about the situation and no help
 * at all with it.
 */
function EmptyLibrary({ onCreate }: { onCreate: () => void }): React.JSX.Element {
  const { text: t } = useLocale();
  return (
    <div className="rounded-panel border-edge bg-surface/60 bg-aurora animate-pop-in grid place-items-center gap-4 border border-dashed px-6 py-14 text-center">
      <span className="text-accent bg-accent-softer grid h-14 w-14 place-items-center rounded-2xl">
        <IconOrbit size={36} />
      </span>
      <div>
        <p className="font-medium">{t('Your ideas start here.', 'Suas ideias começam aqui.')}</p>
        <p className="text-ink-muted mt-1 text-sm">
          {t(
            'Start from your notes, a template, or a blank map. Then study, plan and present.',
            'Comece com suas anotações, um modelo ou um mapa em branco. Depois estude, planeje e apresente.',
          )}
        </p>
      </div>
      <Button tone="primary" size="lg" onClick={onCreate}>
        <IconPlus size={18} />
        {t('Make your first map', 'Crie seu primeiro mapa')}
      </Button>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-12">{children}</main>;
}

function Notice({
  tone,
  children,
}: {
  tone: 'danger' | 'warning';
  children: React.ReactNode;
}): React.JSX.Element {
  const border = tone === 'danger' ? 'border-danger/50' : 'border-warning/60';
  return (
    <p className={`rounded-panel mb-6 border ${border} bg-surface text-ink p-4 text-sm`}>
      {children}
    </p>
  );
}
